import OpenAI from 'openai';
import { retry, delay } from './general';
import VideoCreatorStore from '@src/stores/VideoCreatorStore';

// import process from 'node:process';

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_CHATGPT_API_KEY as string,
  dangerouslyAllowBrowser: true,
  timeout: 60 * 1000,
  maxRetries: 3,
});

export const getGPTFixingSummary = async (
  fullText: string,
  model: string = 'gpt-4o',
) => {
  let params = {
    messages: [
      {
        role: 'system',
        content:
          'You are a transcript editor, you decide how to fix a transcript to what is more likely a proper transcription. You fix mistakes like putting "v" "c" and "r" as seperate "words" when they should together as "VCR". the user will provide you with a json object, at the root are the fields: "storyId", "uploadId", "storyName", "storyTeller", "fullText", and "elemetnts". "fullText" is the full transcript text, "elements" is an array of objects, each object has the fields: "type", "ts", "end_ts", "confidence". "type" is the type of transcript element, it can be either "text" or "punctuation". If it is "text", then it has a "ts" and "end_ts" which are the start and stop timestamp of the text utterance. A "punctuation" type are spaces and punctuation marks. "Confidence" is how confident the original algorithm was that the value was the word actually uttered in the sound file. Summarize the full story first so that all the words make sense in the context of the story.',
      },
      {
        role: 'user',
        content: `Here's the full story text in paragraph form: ${fullText}`,
      },
    ],
    model: model,
    temperature: 0.7,
  };
};

export const getGPTranscriptReplacements = async (
  fullText: string,
  batchText: string,
  storyTranscriptElements: Array<object>,
  model: string = 'gpt-4o',
) => {
  const summary = await getGPTFixingSummary(fullText);

  let params = {
    messages: [
      {
        role: 'system',
        content:
          'You are a transcript editor, you decide how to fix a transcript to what is more likely a proper transcription. You fix mistakes like putting "v" "c" and "r" as seperate "words" when they should together as "VCR". the user will provide you with a json object, at the root are the fields: "storyId", "uploadId", "storyName", "storyTeller", "fullText", and "elemetnts". "fullText" is the full transcript text, "elements" is an array of objects, each object has the fields: "type", "ts", "end_ts", "confidence". "type" is the type of transcript element, it can be either "text" or "punctuation". If it is "text", then it has a "ts" and "end_ts" which are the start and stop timestamp of the text utterance. A "punctuation" type are spaces and punctuation marks. "Confidence" is how confident the original algorithm was that the value was the word actually uttered in the sound file. Summarize the full story first so that all the words make sense in the context of the story.',
      },
      {
        role: 'user',
        content: `Here's the summary of the entire transcript: ${summary}`,
      },
      {
        role: 'user',
        content: `Here's the part of the story we are currently evaluating for fixes in paragraph form: ${batchText}`,
      },
      {
        role: 'user',
        content: `Here's the same part of the story as an array of json objects where each object represents a word, space or punctuation: ${JSON.stringify(
          storyTranscriptElements,
        )}`,
      },
      {
        role: 'user',
        content:
          'Fix the transcript, combining pieces of text that were seperated by accident, fix spellings and proper names? Do not add changes that are not necessary or superflous, like combining words that are not supposed to be combined. if you are unsure, you should use the string_equality function to check if the strings are the same. You do not need to include all words in the transcript, only the ones that need to be changed. If no change needed, do not include it in the changes array when you call the function apply_replacements. If you are not sure, you can skip the change.',
      },
    ],
    functions: [
      {
        name: 'apply_replacements',
        description: 'Apply the specified replacements to the transcript',
        parameters: {
          type: 'object',
          properties: {
            changes: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  index: {
                    type: 'number',
                    description:
                      'The starting index in the elements array to apply the change to',
                  },
                  count: {
                    type: 'number',
                    description:
                      'The number of elements to apply the change to',
                  },
                  old_value: {
                    type: 'string',
                    description:
                      'The old value of the change. only required for replace and remove',
                  },
                  new_value: {
                    type: 'string',
                    description:
                      'The new value of the change. only required for replace',
                  },
                  reason: {
                    type: 'string',
                    description: 'The reason for the change. Always required',
                  },
                },
              },
            },
          },
          required: ['changes', 'reason'],
        },
      },
    ],
    function_call: { name: 'apply_replacements' },
    model: model,
    temperature: 0.3,
  };

  const completion = await openai.chat.completions.create(
    params as OpenAI.ChatCompletionCreateParamsNonStreaming,
  );
  console.log('got completion', completion);
  const response = completion.choices[0].message;
  if (response && response.function_call) {
    if (response.function_call.name === 'apply_replacements') {
      return response.function_call.arguments;
    } else if (response.function_call.name === 'string_equality') {
      try {
        const args = JSON.parse(response.function_call.arguments);
        params.messages.push({
          role: 'assistant',
          content: string_equality(args.a, args.b),
        });
        return await openai.chat.completions.create(
          params as OpenAI.ChatCompletionCreateParamsNonStreaming,
        );
      } catch (error: any) {
        console.log('error parsing arguments', error);
      }
    }
  } else if (response && response.content) {
    console.log('gpt had an idea');
    params.messages.push(response as any);
    return await openai.chat.completions.create(
      params as OpenAI.ChatCompletionCreateParamsNonStreaming,
    );
  }
  return response;
};

export const string_equality = (a: string, b: string) => {
  if (!a || !b) {
    // call chatgpt again with the new message of the inputs being bad
    return `The strings you provided are invalid. Try again.`;
  } else if (a === b) {
    // call chatgpt again with the new message of the strings being equal
    return `The strings "${a}" and "${b} are equal, please continue.`;
  } else {
    // call chatgpt again with the new message of the strings being not equal
    return `The strings "${a}" and "${b} are not equal, please continue.`;
  }
};
export const gpt_functions = [
  {
    name: 'word_count',
    description: 'Count how many words are in a string',
    parameters: {
      type: 'object',
      properties: {
        string: {
          type: 'string',
          description: 'The string to count the words in',
        },
      },
      required: ['string'],
    },
  },
  {
    name: 'string_equality',
    description: 'Check if two strings are equal',
    parameters: {
      type: 'object',
      properties: {
        a: {
          type: 'string',
          description: 'the first string',
        },
        b: {
          type: 'string',
          description: 'the second string',
        },
      },
      required: ['a', 'b'],
    },
  },
];

export const getNewVideoOriginal = async (
  story: any,
  storyTranscription: string,
  messages: any[],
  systemMessage: string,
  userMessage: string,
  userPrompt: string,
  videoLength: string,
  model: string = 'gpt-4o',
) => {
  const testing = true;
  if (testing) {
    if (storyTranscription) {
      const originalVideoWords = storyTranscription.split(' ').length;
      const newVideoWords = Math.round(
        (originalVideoWords * parseFloat(videoLength)) /
          parseFloat(story.originalVideo?.video?.duration),
      );

      let allMessages: any[] = [
        { role: 'system', content: systemMessage },
        {
          role: 'user',
          content: `${userPrompt
            .replace('{{newVideoWords}}', newVideoWords.toString())
            .replace(
              '{{originalVideoWords}}',
              originalVideoWords.toString(),
            )} \n ${storyTranscription}`,
        },
      ];
      if (messages.length > 0) {
        allMessages = [
          ...allMessages,
          ...messages.map((message: any) => {
            let role = message.sender === 'You' ? 'user' : 'assistant';
            return { role: role, content: message.content };
          }),
        ];
      }

      if (userMessage && userMessage.length > 0) {
        allMessages = [...allMessages, { role: 'user', content: userMessage }];
      }

      console.log('allMessages', allMessages);

      // GET chat completion from GPT
      const completion = await openai.chat.completions.create({
        // @ts-ignore
        messages: allMessages,
        model: model,
        temperature: 0.66,
      });
      let response = completion.choices[0]?.message?.content;
      if (response) {
        let resultWords = response.split(' ').length;
        let tries = 0;
        while (
          (resultWords <= newVideoWords * 0.6 ||
            resultWords >= newVideoWords * 1.4) &&
          tries < 5
        ) {
          if (resultWords < newVideoWords) {
            allMessages = [
              ...allMessages,
              ...[
                { role: 'assistant', content: response },
                {
                  role: 'user',
                  content: `That version has too few words. It has ${resultWords} words, the story needs to have only ${newVideoWords} words. Can you edit the original story again, making sure to only take out words and not change conjugation or spelling, but make it longer?`,
                },
              ],
            ];
          } else {
            allMessages = [
              ...allMessages,
              ...[
                { role: 'assistant', content: response },
                {
                  role: 'user',
                  content: `That version has too many words. It has ${resultWords} words, the story needs to have only ${newVideoWords} words. Can you edit the original story again, making sure to only take out words and not change conjugation or spelling, but make it shorter?`,
                },
              ],
            ];
          }
          console.log('TRYING AGAIN', allMessages);
          const completion = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });

          response = completion.choices[0].message.content;
          if (response) {
            resultWords = response.split(' ').length;
          }
          tries += 1;
        }
      }
      return response;
    }
  } else {
    return `Every summer, my family would visit Onset, Massachusetts, near Cape Cod. We traveled in our 2002 Ford Windstar minivan, bringing a small TV with a VCR player. As the second oldest, I didn't get to pick movies. We loaded up the car with my parents, older sister, younger sister, and little brother. We arrived at my grandfather's one-story home near the bay, with a view of the water from the dead end street. He had an RV on his large property, and we fought over who got to stay in it. The RV had a cool interior with a kitchen, storage areas, and a queen-size mattress. I never got to stay there, but I would eat and build forts inside it. On our way back to New Jersey, my little brother asked for an "avi," thinking it meant the RV. We drove through Providence, and he thought he meant an Avis car rental. It was always a treat to visit my grandfather, despite the long journey.`;
  }
};

const getFinalNewStory: any = async (
  allMessages: any[],
  tries = 0,
  model: string = 'gpt-4o',
) => {
  // GET chat completion from GPT
  console.log('getting final', allMessages);
  let completionEnd: any;
  await retry(async () => {
    completionEnd = await openai.chat.completions.create({
      // @ts-ignore
      messages: allMessages,
      model: model,
      temperature: 0.66,
      functions: [
        {
          name: 'replace_sentences',
          description:
            'For each sentence in a condensed story replace it with a sentence in the original transcript that most closely matches it.',
          parameters: {
            type: 'object',
            properties: {
              changes: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    new_sentence: {
                      type: 'string',
                      description:
                        'The new sentence in a condensed version of the story.',
                    },
                    original_sentence: {
                      type: 'string',
                      description:
                        'The sentence in the original transcript that most closely matches the content, meaning and purpose of the new sentence.',
                    },
                    reason: {
                      type: 'string',
                      description:
                        'The reason the original sentence is the best match for the new sentence.',
                    },
                  },
                },
              },
            },
            required: ['changes', 'reason'],
          },
        },
      ],
      function_call: { name: 'replace_sentences' },
    });
  });

  try {
    const response = completionEnd.choices[0].message;
    if (
      response &&
      response.function_call &&
      response.function_call.name === 'replace_sentences'
    ) {
      const changes = JSON.parse(response.function_call.arguments);
      console.log('changes', changes);
      return changes.changes
        .map((change: any) => change.original_sentence)
        .join(' ');
    } else {
      throw 'No response or bad from GPT';
    }
  } catch (error: any) {
    if (tries < 5) {
      console.log('error parsing arguments', error);
      console.log('trying again', tries);
      return await getFinalNewStory(allMessages, tries + 1);
    } else {
      console.log('error parsing arguments', error);
      console.log('tried 5 times, giving up');
      return 'Error parsing arguments';
    }
  }
};

type AIProducer2Prompts = {
  systemMessage: string;
  userMessage: string;
  userPrompt: string;
  videoLength: string;
  storyQuestions: string;
  aiQuestionUnderstanding: string;
  giveAITranscript: string;
  askForAnswers: string;
  askForOutline: string;
  askForStory: string;
  askForSimpleParagraph: string;
  getOriginalSententences: string;
};

export const getNewVideo = async (
  story: any,
  storyTranscription: string,
  messages: any[],
  prompts: AIProducer2Prompts,
  model: string = 'gpt-4o',
) => {
  const testing = true;
  const force_parts = false;
  console.log('using story', story.id);
  if (story.id === 'npi3NcEkT_qs4p08qZFT0g') {
    await delay(5000 + Math.round(5 * Math.random()) * 1000);

    return `Lucy's experience with wheelchair racing was totally accidental. We've lived in northern Manhattan. It happened to be a, a November Sunday morning. We were walking up fifth Avenue, which was totally empty, and it's never empty, and we had turned on that. It was Marathon Sunday. Able-bodied runners have not made it to that part of the, the city, but the first leg of the race is always the wheelchair racing. We didn't realize that it was a wheelchair racing sport. Lucy whispered to herself, what is this? What am I watching? And then she whispered, I'm gonna do that. One day, the next day, my wife and I contacted the New York Roadrunners and they sent us information for their youth wheelchair racing team. They invited her out for her practice. Lucy quickly fell in love with it. Lucy's physical therapist reached out to us and informed us that there was a great nonprofit called JDJ Travel Foundation that provides wheelchairs for children and our physical therapists. Today, a lot of the kids find, you know, their role models or heroes or people they look up to through social media. There's a lot of fan girly one, a professional like s Seroni or some of the other professionals, you know, will respond to Lucy's stories. I think Lucy wants to create content to bring exposure to the sport because so many people don't realize it's possible. There's so many kids in wheelchairs who don't realize this is, this is a fan. We live in New York City. Our team is a team of four, maybe five kids. There are more than five kids in New York City wheelchairs. And it's just trying to get the exposure and, and let people know who they are, or Instagram account that she uses. She'll connect with kids from junior national and engage in conversations throughout the year. it's moved from racing to just wanting to do adventures. So while she loves everything pink, she also loves to go rock climbing. And JDJ being the incredible organization that they are has donated, it's called a sit ski. Sit Ski is basically a chair sitting on a ski and wheelchair. Users can still go out and enjoy winter sports. Lucy is now regularly skiing. I think one day she hopes to be a, a two sport Paralympian from racing the summers and downhill skating in the winters. These kids are kids having fun and working hard at something they love and, and we're gonna support 'em as parents and we're, we're gonna continue to support them.`;
  }
  if (testing) {
    if (storyTranscription) {
      const originalVideoWords = storyTranscription.split(' ').length;
      const newVideoWords = Math.round(
        (originalVideoWords * parseFloat(prompts.videoLength)) /
          parseFloat(story.originalVideo?.video?.duration),
      );

      let allMessages: any[] = [
        { role: 'system', content: prompts.systemMessage },
        { role: 'user', content: prompts.storyQuestions },
        { role: 'assistant', content: prompts.aiQuestionUnderstanding },
        {
          role: 'user',
          content: prompts.giveAITranscript.replace(
            '{{transcript}}',
            storyTranscription,
          ),
        },
      ];

      let completion1Text: any = null;
      if (force_parts) {
        completion1Text = `Yes, I understand the story. It is about a family's annual summer trips to Onset, Massachusetts to visit their grandfather. The family would load up into their minivan, bringing along a small TV with a VCR player to watch movies during the journey. The protagonist, the second oldest sibling, would often defer to their younger siblings' movie choices. Upon arriving in Onset, they would stay at their grandfather's property, which had limited sleeping accommodations. Their grandfather had an RV parked at the end of a dead-end street, and the family would often fight over who got to stay in it. The RV held a special place in the protagonist's heart, even though they never got to stay in it themselves. The story concludes with a humorous misunderstanding involving the protagonist's younger brother mispronouncing "RV" as "avi." The trips to visit their grandfather were cherished family occasions for the protagonist, despite the long journey and limited sleeping arrangements.`;
      }

      if (!completion1Text) {
        // GET chat completion from GPT
        await retry(async () => {
          const completion1 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });
          completion1Text = completion1.choices[0].message.content || '';
        });
      }
      console.log('completion 1', completion1Text);

      // add the response to the messages, and ask for the answers
      allMessages = [
        ...allMessages,
        { role: 'assistant', content: completion1Text },
        { role: 'user', content: prompts.askForAnswers },
      ];

      let completion2Text: any = null;
      if (force_parts) {
        completion2Text = `1. Purpose: The purpose of the story is to reminisce about the family's annual summer trips to Onset, Massachusetts and the special memories associated with those visits.

        2. Perspective: The story provides an intimate look at the protagonist's perspective as they recall the excitement and anticipation of the trips, as well as their connection to their grandfather and the RV on his property.
        
        3. Conflict or Struggle: The conflict in the story revolves around the limited sleeping accommodations at the grandfather's property, which leads to the family members fighting over who gets to stay in the RV.
        
        4. Resolution: The conflict is resolved through the family members taking turns staying in the RV, with the protagonist never getting the chance to experience it fully themselves.
        
        5. Authenticity: The story feels authentic as it is told from the first-person perspective of someone reminiscing about their childhood experiences and the genuine emotions associated with those memories.
        
        6. Character Development: The protagonist, as the second oldest sibling, showcases their willingness to defer to their younger siblings' preferences during the trip, demonstrating a sense of maturity and consideration.
        
        7. Engaging Narration: The story is engaging as it is narrated with enthusiasm and fondness, bringing the audience into the world of the family's summer trips and the special significance of the RV.
        
        8. Theme: The theme of the story is the importance of family traditions and the cherished memories created during these shared experiences.
        
        9. Emotional Connection: The audience can connect with the story through the protagonist's nostalgic recollection, evoking their own memories of family trips and the bonds formed with loved ones.
        
        10. Structure: The beginning of the story introduces the family's tradition of summer trips to Onset, Massachusetts. The middle showcases the excitement and conflicts surrounding the limited sleeping arrangements and the coveted RV. The end brings closure as the protagonist reflects on the lasting impact of these trips and the humorous misunderstanding involving their younger brother's mispronunciation.`;
      }

      if (!completion2Text) {
        // GET chat completion from GPT
        await retry(async () => {
          const completion2 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });

          completion2Text = completion2.choices[0].message.content || '';
        });
      }

      console.log('completion 2', completion2Text);

      // add the response to the messages, and ask for the outline
      allMessages = [
        ...allMessages,
        { role: 'assistant', content: completion2Text },
        { role: 'user', content: prompts.askForOutline },
      ];

      let completion3Text: any = null;
      if (force_parts) {
        completion3Text = ` Title: "RV Memories: A Summer Tradition"

        1. Introduction
          - Set the scene of the family's annual summer trips to Onset, Massachusetts.
          - Highlight the purpose of the story: to relive the cherished memories associated with these trips.
        
        2. The Protagonist's Perspective
          - Introduce the protagonist as the second oldest sibling in the family.
          - Share the protagonist's excitement and anticipation for the trips, despite the limited opportunities to see their grandparents.
        
        3. Conflict Emerges
          - Describe the family's mode of transportation, a 2002 Ford Windstar minivan, and the tradition of bringing a small TV with a VCR player.
          - Highlight the conflict of the protagonist losing their right to choose movies during the journey, deferring to their younger siblings' preferences.
        
        4. Arrival at Grandfather's Property
          - Paint a picture of Onset, Massachusetts, a small cottage town on the cusp of Cape Cod.
          - Describe the grandfather's one-story home on a large property, located near a golf course and with a view of the bay.
        
        5. The Coveted RV
          - Introduce the RV parked at the end of the dead-end street on the grandfather's property.
          - Highlight the family members' excitement and the ensuing conflicts over who gets to stay in the RV due to limited sleeping accommodations.
        
        6. The RV's Allure
          - Provide a vivid description of the RV's interior, with its upholstered seats, kitchen, storage areas, and a queen-size mattress at the back.
          - Share the protagonist's yearning to experience the RV, even if they never got the chance due to their parents' preference for privacy.
        
        7. Authenticity of the Experience
          - Emphasize the authenticity of the family's trips, with genuine moments of enjoying meals inside the RV, building forts, and creating lasting memories.
        
        8. The Protagonist's Growth
          - Highlight the protagonist's maturity and consideration as they always deferred to their younger siblings' desires during the trip.
        
        9. Emotional Connection and Theme
          - Evoke emotions by reflecting on the significance of family traditions and the lasting impact of these shared experiences.
          - Emphasize the theme of the story: the importance of family bonds and the memories created during these annual summer trips.
        
        10. Resolution and Humorous Misunderstanding
          - Conclude the story with the family's journey back to New Jersey, driving through an urban area.
          - Introduce the humorous misunderstanding as the protagonist's younger brother mispronounces "RV" as "avi," leading to confusion and laughter.
          
        11. Conclusion
          - Reflect on the significance of the trips and the family's appreciation for the time spent with their grandfather.
          - Reiterate the purpose of the story: to preserve and share the cherished memories of these summer traditions.
        
        By following this outline, the story will flow smoothly, providing a clear beginning, middle, and end while addressing all the 10 questions and maintaining the proper story structure.`;
      }

      if (!completion3Text) {
        // GET chat completion from GPT
        await retry(async () => {
          const completion3 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });

          completion3Text = completion3.choices[0].message.content || '';
        });
      }

      console.log('completion 3', completion3Text);

      // add the response to the messages
      allMessages = [
        ...allMessages,
        { role: 'assistant', content: completion3Text },
      ];

      // add userMessage if it exists
      if (prompts.userMessage && prompts.userMessage.length > 0) {
        allMessages = [
          ...allMessages,
          { role: 'user', content: prompts.userMessage },
        ];
      }

      // ask for story
      allMessages = [
        ...allMessages,
        { role: 'user', content: prompts.askForStory },
      ];

      let completion4Text: any = null;
      if (force_parts) {
        completion4Text = `Title: "RV Memories: A Summer Tradition"

        Once upon a time, every summer my family would take a visit to Onset, Massachusetts. We lived about four and a half, five hours away in New Jersey. These trips were something that my siblings and I always looked forward to. We would load up into our 2002 Ford Windstar minivan. A big component of the trip was always bringing one of our small TVs with the VCR player. We would all watch movies during the journey.
        
        As the second oldest, I lost my right to pick the movies. It was always my younger siblings who got to choose. We would usually have time for two or three videos to play. We made a stop along the way. It was always my younger siblings who decided what to watch.
        
        We finally arrived in Onset. My grandfather's home was located at the top of a dead end, right off the bay. It was a small one-story house on a large property near a golf course. Unfortunately, there wasn't always enough room for all of us to sleep comfortably. That's when my grandfather's RV came into play.
        
        The RV was parked at the end of the dead end. It was so cool! We all fought over who got to stay in it. I never got the chance, but it held a special place in my heart. It felt like going back 30 years, like something out of "That '70s Show." The RV had upholstered seats, a little kitchen, and storage areas. At the back, there was a queen-size mattress. You could close the curtain and see the little roof. It was a treat to stay in the RV.
        
        Even though I never got to stay there, I would always try to find a way to spend time inside. I would take my food and eat inside, or go up to the upper deck. I would close the curtain and build a fort. It was a really beautiful place in my heart.
        
        On our way back, we bungee corded the TV around the seats in the car. We were driving through an urban area, maybe Providence, Rhode Island. My little brother, who was turning 23, almost 24, chimed up. He said, "Abba, can we get an avi?" I was confused and asked, "What's that?" He meant to say "RV," but he pronounced it as "avi." It was a funny moment, with my dad's Bostonian accent still present.
        
        Those trips to visit my grandfather were always cherished occasions. Despite the long journey and limited sleeping arrangements, they held a special place in our hearts. They were our family tradition, our summer trips.
        
        And that's the story of our RV memories, a summer tradition that brought us closer as a family.`;
      }

      if (!completion4Text) {
        // GET chat completion from GPT
        await retry(async () => {
          const completion4 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });

          completion4Text = completion4.choices[0].message.content || '';
        });
      }

      console.log('completion 4', completion4Text);

      // reset allMessages because likely too long, and ask for simple paragraph
      allMessages = [
        { role: 'system', content: prompts.systemMessage },
        {
          role: 'user',
          content: prompts.giveAITranscript.replace(
            '{{transcript}}',
            storyTranscription,
          ),
        },
        { role: 'user', content: prompts.askForStory },
        { role: 'assistant', content: completion4Text },
        { role: 'user', content: prompts.askForSimpleParagraph },
      ];

      if (messages.length > 0) {
        allMessages = [
          ...allMessages,
          ...messages.map((message: any) => {
            let role = message.sender === 'You' ? 'user' : 'assistant';
            return { role: role, content: message.content };
          }),
        ];
      }

      let completion5Text: any = null;
      if (force_parts) {
        completion5Text = `Growing up every summer my family would take a visit to Onset, Massachusetts, just at the cusp of what people would consider Cape Cod. We lived four and a half, five hours away in New Jersey. We loaded up into our 2002 Ford Windstar minivan, bringing a small TV with a VCR player for the trip. As the second oldest, I lost my right to pick the movies. We made it to my grandfather's small one-story home on a large property right off a golf course. There wasn't always room for all of us, so my grandfather had an RV parked at the end of the dead end. We all fought over who got to stay in the RV. It felt like going back 30 years, like something out of "That '70s Show." Inside, there were upholstered seats, a little kitchen, and storage areas. At the back, there was a queen-size mattress. On our way back, we bungee corded the TV around the seats. My little brother, who was turning 23, almost 24, asked if we could get an "avi." We all laughed because he meant an RV. Those trips were always cherished occasions, our family tradition.`;
      }

      if (!completion5Text) {
        // GET chat completion from GPT
        await retry(async () => {
          const completion5 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });

          completion5Text = completion5.choices[0].message.content || '';
        });
      }

      console.log('completion 5', completion5Text);

      // try to get the proper length story
      allMessages = [
        { role: 'system', content: prompts.systemMessage },
        {
          role: 'user',
          content: prompts.giveAITranscript.replace(
            '{{transcript}}',
            storyTranscription,
          ),
        },
        { role: 'user', content: prompts.askForStory },
        { role: 'assistant', content: completion5Text },
        {
          role: 'user',
          content: `${prompts.userPrompt
            .replace(/{{newVideoWords}}/g, newVideoWords.toString())
            .replace(
              /{{originalVideoWords}}/g,
              originalVideoWords.toString(),
            )}`,
        },
      ];

      let targetStory: any = completion5Text;
      if (targetStory) {
        let resultWords = targetStory.split(' ').length;
        let tries = 0;
        while (
          (resultWords <= newVideoWords * 0.6 ||
            resultWords >= newVideoWords * 1.4) &&
          tries < 5
        ) {
          if (resultWords < newVideoWords) {
            allMessages = [
              { role: 'system', content: prompts.systemMessage },
              {
                role: 'user',
                content: prompts.giveAITranscript.replace(
                  '{{transcript}}',
                  storyTranscription,
                ),
              },
              { role: 'user', content: prompts.askForStory },
              { role: 'assistant', content: targetStory },
              {
                role: 'user',
                content: `That version has too few words. It has ${resultWords} words, the story needs to have only ${newVideoWords} words. Can you edit the original story again, making sure to only take out words and not change conjugation or spelling, but make it longer?`,
              },
            ];
          } else {
            allMessages = [
              { role: 'system', content: prompts.systemMessage },
              {
                role: 'user',
                content: prompts.giveAITranscript.replace(
                  '{{transcript}}',
                  storyTranscription,
                ),
              },
              { role: 'user', content: prompts.askForStory },
              { role: 'assistant', content: targetStory },
              {
                role: 'user',
                content: `That version has too many words. It has ${resultWords} words, the story needs to have only ${newVideoWords} words. Can you edit the original story again, making sure to only take out words and not change conjugation or spelling, but make it shorter?`,
              },
            ];
          }
          // console.log('TRYING AGAIN', allMessages);
          await retry(async () => {
            const completionX = await openai.chat.completions.create({
              // @ts-ignore
              messages: allMessages,
              model: model,
              temperature: 0.66,
            });
            targetStory = completionX.choices[0].message.content || '';
          });

          if (targetStory) {
            resultWords = targetStory.split(' ').length;
          }
          tries += 1;
          console.log('completion X ', tries + 1, targetStory, resultWords);
        }
      }

      // add the response to the messages, and ask for original sentences
      allMessages = [
        { role: 'system', content: prompts.systemMessage },
        {
          role: 'user',
          content: prompts.giveAITranscript.replace(
            '{{transcript}}',
            storyTranscription,
          ),
        },
        { role: 'user', content: prompts.askForStory },
        { role: 'assistant', content: targetStory },
        { role: 'user', content: prompts.getOriginalSententences },
      ];

      let finalNewStory: any = null;
      if (force_parts) {
        finalNewStory = `Growing up every summer my family would take a visit to onset massachusetts which is kind of just at the cusp of what people would consider cape cod because we lived about four and a half five hours away in new jersey the way that we traveled as a family is we loaded up into our 2002 ford windstar minivan and a big component of the trip was always bringing one of our small tvs with the vcr player but here's the thing as the second oldest i lost my right to pick because i wouldn't complain as much and it was always my younger siblings so we finish a trip and and we make it to onset my grandfather lives up a street right off of right off of the bay and you could see just at the he lives at the top so while it's a very large property there wasn't always room for all of us for it to be accommodated for sleeping so what did he do he on his property he had an rv and the rv was parked at the end of the dead end so the rv was so cool and you know this is like the mid two thousands maybe 2004 2005 2006 and it felt like i was going back 30 years to what i would see on like that that 70 show or something and you walk in and you have like the really cool like upholstered seat and you have a little kitchen and then you have all the storage areas and all the way in the back there's a queen size mattress in it you can close the curtain and a chart says little roof so that's what everyone in the family would fight over the rv was so cool and you know this is like the mid two thousands maybe 2004 2005 2006 and it felt like i was going back 30 years to what i would see on like that that 70 show or something on our way back we were filling in the car we all filled in the car and we put the tv little box television we had a bungee corded around the seats my little brother who's now he's turning he's he's now 23 almost 24 he says abba that's da dad in he hebrew abba can we get an avi i'm like and i'm like a what he's like i want an avi when we get back and we're like what's that i'm like i thought like i remember i'm like maybe 12 we drove by like an avis car rental and we're like my dad's like yeah you what what do you mean an avi avi's a very common jewish name i'm like who's avi we all have friends there like what he's like no you know like the little the the car that we slept in in edie's grand grandpa did edie's driveway like oh you mean in rv not an av like oh my gosh that accent that bostonian accent that my dad got rid of but my grandfather never got rid of it was always do you wanna stay in the avi the avi this the avi that and my brother at six years old he thought it was called an avi it was always an occasion and a treat to be able to see my grandfather he living five hours away being one of four children it's quite a trip it's quite a journey to go with you know for for my parents but we always made the time having that whole trip like some people they go to they go skiing and whistler or they go to key west on the drive down to all the way down to boston florida or they go wherever they but for my family it was it was that was our trip that was our family`;
      }

      if (!finalNewStory) {
        finalNewStory = await getFinalNewStory(allMessages);
      }
      console.log('finalNewStory', finalNewStory);

      return finalNewStory || '';
    }
  } else {
    return `Every summer, my family would visit Onset, Massachusetts, near Cape Cod. We traveled in our 2002 Ford Windstar minivan, bringing a small TV with a VCR player. As the second oldest, I didn't get to pick movies. We loaded up the car with my parents, older sister, younger sister, and little brother. We arrived at my grandfather's one-story home near the bay, with a view of the water from the dead end street. He had an RV on his large property, and we fought over who got to stay in it. The RV had a cool interior with a kitchen, storage areas, and a queen-size mattress. I never got to stay there, but I would eat and build forts inside it. On our way back to New Jersey, my little brother asked for an "avi," thinking it meant the RV. We drove through Providence, and he thought he meant an Avis car rental. It was always a treat to visit my grandfather, despite the long journey.`;
  }
};

type AIProducer3Prompts = {
  systemMessage: string;
  userMessage: string;
  userPrompt: string;
  videoLength: string;
  getOriginalSententences: string;
};

export const getNewVideo3 = async (
  story: any,
  storyTranscription: string,
  messages: any[],
  prompts: AIProducer3Prompts,
  model: string = 'gpt-4o',
) => {
  const testing = true;
  const force_parts = false;
  console.log('using story', story.id);
  if (story.id === 'npi3NcEkT_qs4p08qZFT0g') {
    await delay(5000 + Math.round(5 * Math.random()) * 1000);

    return `Lucy's experience with wheelchair racing was totally accidental. We've lived in northern Manhattan. It happened to be a, a November Sunday morning. We were walking up fifth Avenue, which was totally empty, and it's never empty, and we had turned on that. It was Marathon Sunday. Able-bodied runners have not made it to that part of the, the city, but the first leg of the race is always the wheelchair racing. We didn't realize that it was a wheelchair racing sport. Lucy whispered to herself, what is this? What am I watching? And then she whispered, I'm gonna do that. One day, the next day, my wife and I contacted the New York Roadrunners and they sent us information for their youth wheelchair racing team. They invited her out for her practice. Lucy quickly fell in love with it. Lucy's physical therapist reached out to us and informed us that there was a great nonprofit called JDJ Travel Foundation that provides wheelchairs for children and our physical therapists. Today, a lot of the kids find, you know, their role models or heroes or people they look up to through social media. There's a lot of fan girly one, a professional like s Seroni or some of the other professionals, you know, will respond to Lucy's stories. I think Lucy wants to create content to bring exposure to the sport because so many people don't realize it's possible. There's so many kids in wheelchairs who don't realize this is, this is a fan. We live in New York City. Our team is a team of four, maybe five kids. There are more than five kids in New York City wheelchairs. And it's just trying to get the exposure and, and let people know who they are, or Instagram account that she uses. She'll connect with kids from junior national and engage in conversations throughout the year. it's moved from racing to just wanting to do adventures. So while she loves everything pink, she also loves to go rock climbing. And JDJ being the incredible organization that they are has donated, it's called a sit ski. Sit Ski is basically a chair sitting on a ski and wheelchair. Users can still go out and enjoy winter sports. Lucy is now regularly skiing. I think one day she hopes to be a, a two sport Paralympian from racing the summers and downhill skating in the winters. These kids are kids having fun and working hard at something they love and, and we're gonna support 'em as parents and we're, we're gonna continue to support them.`;
  }
  if (testing) {
    if (storyTranscription) {
      const originalVideoWords = storyTranscription.split(' ').length;
      const newVideoWords = Math.round(
        (originalVideoWords * parseFloat(prompts.videoLength)) /
          parseFloat(story.originalVideo?.video?.duration),
      );

      let allMessages: any[] = [
        { role: 'system', content: prompts.systemMessage },
        {
          role: 'user',
          content: prompts.userPrompt.replace(
            '{{transcript}}',
            storyTranscription,
          ),
        },
      ];

      if (messages.length > 0) {
        allMessages = [
          ...allMessages,
          ...messages.map((message: any) => {
            let role = message.sender === 'You' ? 'user' : 'assistant';
            return { role: role, content: message.content };
          }),
        ];
      }

      if (prompts.userMessage && prompts.userMessage.length > 0) {
        allMessages = [
          ...allMessages,
          { role: 'user', content: prompts.userMessage },
        ];
      }

      let completion1Text: any = null;
      if (force_parts) {
        completion1Text = ``;
      }

      if (!completion1Text) {
        // GET chat completion from GPT
        await retry(async () => {
          const completion1 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
          });
          completion1Text = completion1.choices[0].message.content || '';
        });
      }
      console.log('completion 1', completion1Text);

      // add the response to the messages, and ask for the answers
      allMessages = [
        ...allMessages,
        { role: 'assistant', content: completion1Text },
        { role: 'user', content: prompts.getOriginalSententences },
      ];

      let finalNewStory: any = null;
      if (force_parts) {
        finalNewStory = ``;
      }

      if (!finalNewStory) {
        finalNewStory = await getFinalNewStory(allMessages);
      }
      console.log('finalNewStory', finalNewStory);

      return finalNewStory || '';
    }
  } else {
    return `Every summer, my family would visit Onset, Massachusetts, near Cape Cod. We traveled in our 2002 Ford Windstar minivan, bringing a small TV with a VCR player. As the second oldest, I didn't get to pick movies. We loaded up the car with my parents, older sister, younger sister, and little brother. We arrived at my grandfather's one-story home near the bay, with a view of the water from the dead end street. He had an RV on his large property, and we fought over who got to stay in it. The RV had a cool interior with a kitchen, storage areas, and a queen-size mattress. I never got to stay there, but I would eat and build forts inside it. On our way back to New Jersey, my little brother asked for an "avi," thinking it meant the RV. We drove through Providence, and he thought he meant an Avis car rental. It was always a treat to visit my grandfather, despite the long journey.`;
  }
};

let cursorIndex = 0;
export const getNewVideo4 = async (
  VideoCreator: VideoCreatorStore,
  story: any,
  storyTranscription: string,
  messages: any[],
  prompts: AIProducer3Prompts,
  model: string = 'gpt-4o',
) => {
  const testing = true;
  const force_parts = false;
  console.log('using story', story.id);

  if (testing) {
    if (storyTranscription) {
      const originalVideoWords = storyTranscription.split(' ').length;
      const newVideoWords = Math.round(
        (originalVideoWords * parseFloat(prompts.videoLength)) /
          parseFloat(story.originalVideo?.video?.duration),
      );

      let allMessages: any[] = [
        { role: 'system', content: prompts.systemMessage },
        {
          role: 'user',
          content: prompts.userPrompt
            .replace('{{transcript}}', storyTranscription)
            .replaceAll('{{originalVideoWords}}', originalVideoWords.toString())
            .replaceAll('{{newVideoWords}}', newVideoWords.toString()),
        },
      ];

      if (messages.length > 0) {
        allMessages = [
          ...allMessages,
          ...messages.map((message: any) => {
            let role = message.sender === 'You' ? 'user' : 'assistant';
            return { role: role, content: message.content };
          }),
        ];
      }

      if (prompts.userMessage && prompts.userMessage.length > 0) {
        allMessages = [
          ...allMessages,
          { role: 'user', content: prompts.userMessage },
        ];
      }

      let completion1Text: any = null;
      if (force_parts) {
        completion1Text = ``;
      }

      if (!completion1Text) {
        console.log('allMessages', allMessages);
        // GET chat completion from GPT
        await retry(async () => {
          const completion1 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
            stream: true,
          });

          let part = '';
          for await (const chunk of completion1) {
            part += chunk.choices[0]?.delta?.content || '';
            // console.log('result so far', part)
            // if part includes a period get the first part, but leave rest as part
            // any sentence ender
            if (
              part.includes('.') ||
              part.includes('?') ||
              part.includes('!')
            ) {
              let split_char = '';
              if (part.includes('.')) {
                split_char = '.';
              } else if (part.includes('?')) {
                split_char = '?';
              } else if (part.includes('!')) {
                split_char = '!';
              }
              completion1Text = part.split(split_char)[0];
              part = part.split(split_char).slice(1).join(split_char);
              // call move sentence
              // console.log('streamed sentence', completion1Text)
              askForSentence(
                VideoCreator,
                allMessages,
                completion1Text,
                model,
                prompts,
              );
            }
          }
        });
      }
    }
  }
};

const getMatchedSentence = async (
  videoCreator: VideoCreatorStore,
  messages: any[],
  sentence: string,
  model: string,
  prompts: AIProducer3Prompts,
  attempts: number = 0,
) => {
  if (
    !videoCreator.fuse &&
    videoCreator.videoTranscriptionProcessor?.finalTranscriptionElements
  ) {
    videoCreator.initializeFuse(
      videoCreator.videoTranscriptionProcessor.finalTranscriptionElements,
    );
  }

  const result = videoCreator.fuse.search(sentence);
  // console.log('result', result);
  if (result.length > 0) {
    const bestMatch = result[0];
    // move sentence
    console.log('matched sentence', sentence, 'WITH ', bestMatch.item.text);
    console.log('cutting', cursorIndex, bestMatch.item.startIndex - 1);
    videoCreator.cutTranscriptAndVideo(
      cursorIndex,
      bestMatch.item.startIndex - 1,
    );
    cursorIndex = bestMatch.item.endIndex + 1;
  }
};

const askForSentence = async (
  videoCreator: VideoCreatorStore,
  messages: any[],
  sentence: string,
  model: string,
  prompts: AIProducer3Prompts,
  attempts = 0,
) => {
  // ask chatgpt for sentence match
  // ask user for sentence match
  messages = [
    ...messages,
    {
      role: 'assistant',
      content: sentence,
    },
    {
      role: 'user',
      content: prompts.getOriginalSententences,
    },
  ];
  await retry(async () => {
    const completion1 = await openai.chat.completions.create({
      // @ts-ignore
      messages: messages,
      model: model,
      temperature: 0.66,
    });
    let completion1Text = completion1.choices[0].message.content || '';
    getMatchedSentence(
      videoCreator,
      messages,
      completion1Text,
      model,
      prompts,
      attempts,
    );
  });
};

export const getNewVideo5 = async (
  videoCreator: VideoCreatorStore,
  story: any,
  storyTranscription: string,
  messages: any[],
  prompts: AIProducer3Prompts,
  model: string = 'gpt-4o',
) => {
  const testing = true;
  const force_parts = false;
  console.log('using story', story.id);
  // debugger;
  if (testing) {
    if (storyTranscription) {
      const originalVideoWords = storyTranscription.split(' ').length;
      const newVideoWords = Math.round(
        (originalVideoWords * parseFloat(prompts.videoLength)) /
          parseFloat(story.originalVideo?.video?.duration),
      );

      let allMessages: any[] = [
        { role: 'system', content: prompts.systemMessage },
        {
          role: 'user',
          content: prompts.userPrompt
            .replace('{{transcript}}', storyTranscription)
            .replaceAll('{{originalVideoWords}}', originalVideoWords.toString())
            .replaceAll('{{newVideoWords}}', newVideoWords.toString()),
        },
      ];

      if (messages.length > 0) {
        allMessages = [
          ...allMessages,
          ...messages.map((message: any) => {
            let role = message.sender === 'You' ? 'user' : 'assistant';
            return { role: role, content: message.content };
          }),
        ];
      }

      if (prompts.userMessage && prompts.userMessage.length > 0) {
        allMessages = [
          ...allMessages,
          { role: 'user', content: prompts.userMessage },
        ];
      }

      let completion1Text: any = null;
      if (force_parts) {
        completion1Text = ``;
      }

      if (!completion1Text) {
        console.log('allMessages', allMessages);
        // GET chat completion from GPT
        await retry(async () => {
          const completion1 = await openai.chat.completions.create({
            // @ts-ignore
            messages: allMessages,
            model: model,
            temperature: 0.66,
            stream: true,
          });

          let part = '';
          for await (const chunk of completion1) {
            part += chunk.choices[0]?.delta?.content || '';
            // console.log('result so far', part)
            // if part includes a period get the first part, but leave rest as part
            // any sentence ender
            if (
              part.includes('.') ||
              part.includes('?') ||
              part.includes('!')
            ) {
              let split_char = '';
              if (part.includes('.')) {
                split_char = '.';
              } else if (part.includes('?')) {
                split_char = '?';
              } else if (part.includes('!')) {
                split_char = '!';
              }
              completion1Text = part.split(split_char)[0];
              part = part.split(split_char).slice(1).join(split_char);
              // call move sentence
              // console.log('streamed sentence', completion1Text)
              askForSentence(
                videoCreator,
                allMessages,
                completion1Text,
                model,
                prompts,
              );
            }
          }
        });
      }
    }
  }
};
